import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useOrg } from "../../auth/OrgContext";
import { useAuth } from "../../auth/AuthContext";
import {
  Button,
  InputGroup,
  FormGroup,
  Callout,
  Spinner,
} from "@blueprintjs/core";
import MainLayout from "../../components/MainLayout";
import AccountSidebar from "../../sidebars/AccountSidebar";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

export default function OrganizationSettingsPageWrapper() {
  return (
    <MainLayout pageTitle="Account" LeftBar={AccountSidebar}>
      <OrganizationSettings />
    </MainLayout>
  );
}

function OrganizationSettings() {
  const { orgId } = useParams();
  const { orgs } = useOrg();
  const { idToken } = useAuth();
  const org = orgs.find((o) => o.id === orgId);

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<null | {
    intent: "success" | "danger" | "warning";
    message: string;
  }>(null);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<any[] | null>(null);
  const [membersLoading, setMembersLoading] = useState(true);
  const [invites, setInvites] = useState<any[] | null>(null);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [projects, setProjects] = useState<any[] | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectName, setProjectName] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const [fetchedOnce, setFetchedOnce] = useState(false);

  useEffect(() => {
    if (!orgId || !idToken || fetchedOnce) return;

    console.log(
      "[OrgSettings] Fetching members with token:",
      idToken?.slice(0, 10),
      "...",
      "orgId:",
      orgId,
    );

    setMembersLoading(true);
    setInvitesLoading(true);
    setProjectsLoading(true);

    // Fetch members
    fetch(`${API_BASE}/orgs/${orgId}/members`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(idToken ? { "X-Id-Token": idToken } : {}),
      },
      credentials: "include",
    })
      .then(async (res) => {
        console.log("[OrgSettings] Members fetch status:", res.status);
        if (res.ok) {
          const data = await res.json();
          console.log("[OrgSettings] Members fetched:", data);
          setMembers(data);
        } else if (res.status === 401) {
          setStatus({
            intent: "danger",
            message:
              "You are not authorized. Please log in again or refresh the page.",
          });
          setMembers([]);
        } else {
          setMembers([]);
        }
        setMembersLoading(false);
      })
      .catch((err) => {
        console.warn("[OrgSettings] Fetch error:", err);
        setMembers([]);
        setMembersLoading(false);
      });

    // Fetch invites
    fetch(`${API_BASE}/orgs/${orgId}/invites`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(idToken ? { "X-Id-Token": idToken } : {}),
      },
      credentials: "include",
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setInvites(data);
        } else {
          setInvites([]);
        }
        setInvitesLoading(false);
      })
      .catch((err) => {
        setInvites([]);
        setInvitesLoading(false);
      });

    // Fetch projects
    fetch(`${API_BASE}/orgs/${orgId}/projects`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(idToken ? { "X-Id-Token": idToken } : {}),
      },
      credentials: "include",
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setProjects(data);
        } else {
          setProjects([]);
        }
        setProjectsLoading(false);
      })
      .catch(() => {
        setProjects([]);
        setProjectsLoading(false);
      });

    setFetchedOnce(true);
  }, [orgId, idToken, fetchedOnce]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/orgs/${orgId}/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok) {
        if (data.alreadyMember) {
          setStatus({
            intent: "warning",
            message: "User is already a member of this organization.",
          });
        } else if (data.autoAdded) {
          setStatus({
            intent: "success",
            message: "User added to organization successfully.",
          });
        } else if (data.inviteSent) {
          setStatus({
            intent: "success",
            message:
              data.message || "Invite sent to user (pending account creation).",
          });
        } else {
          setStatus({
            intent: "success",
            message: "User invited successfully.",
          });
        }
        setEmail("");
        setFetchedOnce(false); // trigger refetch of member list
      } else {
        if (res.status === 404) {
          setStatus({
            intent: "warning",
            message: data.error || "User must create account first.",
          });
        } else {
          setStatus({
            intent: "danger",
            message: data.error || "Failed to invite user.",
          });
        }
      }
    } catch (err) {
      setStatus({ intent: "danger", message: "Network error." });
    }
    setLoading(false);
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setCreatingProject(true);
    try {
      const res = await fetch(`${API_BASE}/orgs/${orgId}/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ name: projectName }),
      });

      const data = await res.json();

      if (res.ok) {
        setProjectName("");
        setProjects((prev) => (prev ? [data, ...prev] : [data]));
      } else {
        setStatus({ intent: "danger", message: data.error || "Failed to create project." });
      }
    } catch (_err) {
      setStatus({ intent: "danger", message: "Network error." });
    }
    setCreatingProject(false);
  };

  if (!idToken) {
    return (
      <Callout intent="danger">
        You must be logged in to view this page.
      </Callout>
    );
  }

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "2rem 0 2rem 0" }}>
      {status && (
        <Callout
          intent={status.intent}
          style={{ maxWidth: 400, marginBottom: 16 }}
        >
          {status.message}
        </Callout>
      )}
      <div style={{ fontWeight: 600, fontSize: "1.15em", marginBottom: 8 }}>
        {org ? org.name : "Organization not found"}
      </div>
      <div style={{ color: "#888", fontSize: "0.98em", marginBottom: 18 }}>
        Invite new members to your organization:
      </div>
      <form onSubmit={handleInvite} style={{ marginBottom: 32 }}>
        <FormGroup label="Invite user by email" labelFor="invite-email">
          <InputGroup
            id="invite-email"
            placeholder="user@email.com"
            value={email}
            onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
            type="email"
            required
            disabled={loading}
            style={{ maxWidth: 350 }}
          />
        </FormGroup>
        <Button
          type="submit"
          intent="primary"
          loading={loading}
          disabled={!email.trim()}
        >
          Invite
        </Button>
      </form>
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ marginBottom: 12 }}>Projects</h3>
        <form onSubmit={handleCreateProject} style={{ marginBottom: 20 }}>
          <FormGroup label="Create new project" labelFor="project-name">
            <InputGroup
              id="project-name"
              placeholder="Project name"
              value={projectName}
              onChange={(e) => setProjectName((e.target as HTMLInputElement).value)}
              disabled={creatingProject}
              style={{ maxWidth: 350 }}
            />
          </FormGroup>
          <Button type="submit" intent="primary" loading={creatingProject} disabled={!projectName.trim()}>
            Create
          </Button>
        </form>
        {projectsLoading ? (
          <Spinner size={20} />
        ) : projects && projects.length > 0 ? (
          <ul style={{ paddingLeft: 0, listStyle: "none" }}>
            {projects.map((p) => (
              <li key={p.id} style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>{p.name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ color: "#888" }}>No projects found.</div>
        )}
      </div>
      <div style={{ marginTop: 32 }}>
        <h3 style={{ marginBottom: 12 }}>Current Members</h3>
        {membersLoading ? (
          <Spinner size={20} />
        ) : members && members.length > 0 ? (
          <ul style={{ paddingLeft: 0, listStyle: "none" }}>
            {members.map((m) => (
              <li key={m.id} style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>{m.email}</span>
                {m.role && (
                  <span
                    style={{ color: "#888", marginLeft: 8, fontSize: "0.95em" }}
                  >
                    ({m.role})
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ color: "#888" }}>No members found.</div>
        )}
      </div>
      <div style={{ marginTop: 32 }}>
        <h3 style={{ marginBottom: 12 }}>Pending Invites</h3>
        {invitesLoading ? (
          <Spinner size={20} />
        ) : invites && invites.length > 0 ? (
          <ul style={{ paddingLeft: 0, listStyle: "none" }}>
            {invites.map((invite) => (
              <li key={invite.id || invite.email} style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>{invite.email}</span>
                {invite.role && (
                  <span
                    style={{ color: "#888", marginLeft: 8, fontSize: "0.95em" }}
                  >
                    (invited as {invite.role})
                  </span>
                )}
                {invite.expires_at && (
                  <span
                    style={{ color: "#b77", marginLeft: 8, fontSize: "0.9em" }}
                  >
                    (expires {invite.expires_at.slice(0, 10)})
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ color: "#888" }}>No pending invites.</div>
        )}
      </div>
    </div>
  );
}
